import { ArrowRight, Search, SlidersHorizontal } from 'lucide-react';

interface InventoryFilterBarProps {
  selectedType: string;
  setSelectedType: (value: string) => void;
  selectedBrand: string;
  setSelectedBrand: (value: string) => void;
  selectedModel: string;
  setSelectedModel: (value: string) => void;
  brands: string[];
  models: string[];
  /** Tipos com estoque no facet atual (zerokm / seminovo) */
  availableTypes?: string[];
  filteredCount: number;
  hasAdvancedFilters: boolean;
  hasFilters: boolean;
  buttonColor: string;
  onOpenMoreFilters: () => void;
  onClear: () => void;
  onSearch: () => void;
  /** Desktop top bar (Avance-like) vs compact mobile bar */
  variant?: 'top' | 'mobile';
}

const TYPE_OPTIONS = [
  { value: 'all', label: 'Tipo' },
  { value: 'zerokm', label: 'Zero KM' },
  { value: 'seminovo', label: 'Seminovos' },
];

export function InventoryFilterBar({
  selectedType,
  setSelectedType,
  selectedBrand,
  setSelectedBrand,
  selectedModel,
  setSelectedModel,
  brands,
  models,
  availableTypes,
  filteredCount,
  hasAdvancedFilters,
  hasFilters,
  buttonColor,
  onOpenMoreFilters,
  onClear,
  onSearch,
  variant = 'mobile',
}: InventoryFilterBarProps) {
  const handleTypeChange = (value: string) => {
    // Parent (setPendingType) já zera marca/modelo na cascata
    setSelectedType(value);
  };

  const handleBrandChange = (value: string) => {
    setSelectedBrand(value);
    setSelectedModel('all');
  };

  const typeOptions = TYPE_OPTIONS.filter(
    (opt) => opt.value === 'all' || !availableTypes || availableTypes.includes(opt.value)
  );

  const typeValue =
    selectedType === 'all' || typeOptions.some((opt) => opt.value === selectedType)
      ? selectedType
      : 'all';
  const brandValue =
    selectedBrand === 'all' || brands.includes(selectedBrand) ? selectedBrand : 'all';
  const modelValue =
    selectedModel === 'all' || models.includes(selectedModel) ? selectedModel : 'all';

  const isTop = variant === 'top';

  return (
    <div className={`wl7-inv-filter-bar${isTop ? ' wl7-inv-filter-bar--top' : ''}`}>
      <div className="wl7-inv-filter-bar__fields">
        <div className="wl7-inv-filter-bar__field">
          {!isTop && <span className="wl7-inv-filter-bar__label">Tipo</span>}
          <select
            className="wl7-inv-filter-bar__select"
            value={typeValue}
            onChange={(e) => handleTypeChange(e.target.value)}
            aria-label="Tipo de veículo"
          >
            {typeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="wl7-inv-filter-bar__field">
          {!isTop && <span className="wl7-inv-filter-bar__label">Marca</span>}
          <select
            className="wl7-inv-filter-bar__select"
            value={brandValue}
            onChange={(e) => handleBrandChange(e.target.value)}
            aria-label="Marca"
          >
            <option value="all">Marca</option>
            {brands.map((brand) => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
        </div>

        <div className="wl7-inv-filter-bar__field">
          {!isTop && <span className="wl7-inv-filter-bar__label">Modelo</span>}
          <select
            className="wl7-inv-filter-bar__select"
            value={modelValue}
            onChange={(e) => setSelectedModel(e.target.value)}
            disabled={brandValue === 'all'}
            aria-label="Modelo"
          >
            <option value="all">Modelo</option>
            {models.map((model) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
        </div>

        {isTop && (
          <button
            type="button"
            className="wl7-inv-filter-bar__apply"
            style={{ backgroundColor: buttonColor, color: 'var(--wl7-on-button, #fff)' }}
            onClick={onSearch}
            aria-label={`Aplicar filtros — ${filteredCount} veículos`}
          >
            <ArrowRight className="h-5 w-5" />
          </button>
        )}
      </div>

      {!isTop && (
        <div className="wl7-inv-filter-bar__actions">
          <button
            type="button"
            className="wl7-inv-filter-bar__more"
            onClick={onOpenMoreFilters}
            aria-label="Mais filtros"
          >
            <SlidersHorizontal className="h-4 w-4 shrink-0" />
            Mais filtros
            {hasAdvancedFilters && (
              <span className="wl7-inv-filter-bar__more-badge" aria-hidden />
            )}
          </button>

          <div className="wl7-inv-filter-bar__actions-end">
            <button
              type="button"
              className="wl7-inv-filter-bar__clear"
              onClick={onClear}
              disabled={!hasFilters}
            >
              Limpar
            </button>

            <button
              type="button"
              className="wl7-inv-filter-bar__search"
              style={{ backgroundColor: buttonColor }}
              onClick={onSearch}
              aria-label={`Buscar ${filteredCount} veículos`}
            >
              <Search className="h-4 w-4 shrink-0" />
              Buscar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
